import { Link } from "react-router-dom";

const footerLinks = {
  navigate: [
    { label: "Home", href: "/" },
    { label: "Tools", href: "/tools" },
    { label: "Blog", href: "/blog" },
    { label: "Co-Found", href: "/co-found" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/legal/privacy" },
    { label: "Terms of Service", href: "/legal/terms" },
    { label: "Affiliate Disclosure", href: "/legal/affiliates" },
  ],
};

const HomeFooter = () => {
  return (
    <footer className="brutal-border border-b-0 border-l-0 border-r-0 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/favicon.png" alt="Co-Found with Us logo" className="w-8 h-8 rounded-md" />
              <h4 className="font-black text-lg text-foreground">Co-Found with Us</h4>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The step-by-step playbook for founders building with AI as their co-founder.
            </p>
          </div>

          {/* Navigate */}
          <div>
            <h4 className="font-black text-sm text-foreground mb-3 uppercase tracking-[0.08em]">Navigate</h4>
            <ul className="space-y-2">
              {footerLinks.navigate.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-black text-sm text-foreground mb-3 uppercase tracking-[0.08em]">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-black text-sm text-foreground mb-2 uppercase tracking-[0.08em]">Social</h4>
            <p className="text-sm text-muted-foreground mb-3">Follow along as we build in public.</p>
            <ul className="flex flex-row flex-wrap gap-[10px] list-none p-0 m-0">
              <li>
                <a
                  href="https://x.com/cofoundwithai"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on X"
                  className="group inline-flex items-center justify-center w-11 h-11 rounded-[10px] bg-card border border-border text-muted-foreground transition-all duration-[180ms] ease-in-out hover:bg-black hover:text-white hover:border-black"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/cofoundwithus/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className="group inline-flex items-center justify-center w-11 h-11 rounded-[10px] bg-card border border-border text-muted-foreground transition-all duration-[180ms] ease-in-out hover:bg-[#E1306C] hover:text-white hover:border-[#E1306C]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="https://www.reddit.com/user/Co-foundWithUs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Reddit"
                  className="group inline-flex items-center justify-center w-11 h-11 rounded-[10px] bg-card border border-border text-muted-foreground transition-all duration-[180ms] ease-in-out hover:bg-[#FF4500] hover:text-white hover:border-[#FF4500]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M22 11.816c0-1.256-1.021-2.277-2.277-2.277-.593 0-1.122.24-1.526.612-1.481-.985-3.455-1.624-5.647-1.706l1.193-3.745 3.276.711a1.81 1.81 0 1 0 .032-.96l-3.65-.792a.5.5 0 0 0-.582.34l-1.36 4.272c-2.347.048-4.471.688-6.046 1.705a2.27 2.27 0 0 0-1.526-.611A2.28 2.28 0 0 0 2 11.816c0 .863.482 1.61 1.19 1.997a4.11 4.11 0 0 0-.082.823c0 3.474 4.046 6.3 9.022 6.3s9.022-2.826 9.022-6.3c0-.275-.029-.55-.082-.82A2.28 2.28 0 0 0 22 11.816zm-14.473 2.21a1.502 1.502 0 1 1 3.005-.05 1.502 1.502 0 0 1-3.005.05zm8.265 4.13c-.86.86-2.385 1.057-3.79 1.057S8.84 19.005 8 18.156c-.165-.165-.165-.43 0-.595s.43-.165.594 0c.535.535 1.685.965 3.21.965s2.675-.43 3.21-.965a.42.42 0 0 1 .593 0 .42.42 0 0 1 0 .595zm-.18-2.61a1.5 1.5 0 1 1-.05-3.005 1.5 1.5 0 0 1 .05 3.005z" />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/company/co-foundwithus/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on LinkedIn"
                  className="group inline-flex items-center justify-center w-11 h-11 rounded-[10px] bg-card border border-border text-muted-foreground transition-all duration-[180ms] ease-in-out hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </li>
              <li>
                <button
                  type="button"
                  disabled
                  aria-label="Discord (Coming soon)"
                  title="Coming soon"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-[10px] bg-card border border-border text-muted-foreground opacity-40 cursor-default"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.036A19.736 19.736 0 0 0 5.93 4.369a.07.07 0 0 0-.032.027C2.533 9.046 1.61 13.58 2.063 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.673-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-foreground/20 pt-6 text-center">
          <p className="font-bold text-sm text-muted-foreground">
            © 2026 Co-Found with Us. All rights reserved. Built with the tools in this book.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default HomeFooter;
